import React from "react";
import { useFetch } from "@/hooks/useApi";
import { PageHeader, StatCard, Loader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { gbp, num, ukDate } from "@/lib/format";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import * as Icons from "lucide-react";

const chartColors = { revenue: "#10B981", costs: "#F43F5E", profit: "#3B82F6", fuel: "#F59E0B" };

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="font-mono" style={{ color: p.color }}>
          {p.name}: {gbp(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const { data, loading } = useFetch("/dashboard/admin");
  if (loading || !data) return <Loader label="Loading dashboard" />;

  return (
    <div className="fp-fade-in">
      <PageHeader title="Fleet Dashboard" subtitle="Live operational & financial overview" testId="admin-dashboard" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard label="Revenue" value={gbp(data.revenue)} icon="PoundSterling" accent="text-emerald-400" testId="stat-revenue" />
        <StatCard label="Total Costs" value={gbp(data.costs)} icon="TrendingDown" accent="text-rose-400" testId="stat-costs" />
        <StatCard label="Net Profit" value={gbp(data.profit)} icon="TrendingUp" accent="text-blue-400" testId="stat-profit" />
        <StatCard label="Fuel Spend" value={gbp(data.fuel_spending)} icon="Fuel" accent="text-amber-400" testId="stat-fuel" />
        <StatCard label="Active Drivers" value={num(data.active_drivers)} icon="Users" testId="stat-drivers" />
        <StatCard label="Active Vans" value={num(data.active_vans)} icon="Truck" testId="stat-vans" />
        <StatCard label="Today's Stops" value={num(data.today_stops)} icon="MapPin" testId="stat-today-stops" />
        <StatCard label="Pending Approvals" value={num(data.pending_approvals)} icon="ClipboardCheck" accent="text-amber-400" testId="stat-pending" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold mb-4">Revenue · Costs · Profit</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.chart}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.revenue} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={chartColors.revenue} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.profit} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={chartColors.profit} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `£${v / 1000}k`} />
              <Tooltip content={<TooltipBox />} />
              <Area type="monotone" dataKey="revenue" stroke={chartColors.revenue} fill="url(#rev)" strokeWidth={2} />
              <Area type="monotone" dataKey="costs" stroke={chartColors.costs} fillOpacity={0} strokeWidth={2} />
              <Area type="monotone" dataKey="profit" stroke={chartColors.profit} fill="url(#prof)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Monthly Fuel Spend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `£${v}`} />
              <Tooltip content={<TooltipBox />} />
              <Bar dataKey="fuel" fill={chartColors.fuel} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icons.Fuel className="h-4 w-4 text-amber-400" />
            <h3 className="font-display font-semibold">Fuel Alerts</h3>
          </div>
          <p className="font-mono text-3xl font-bold text-amber-400">{data.fuel_alerts}</p>
          <p className="text-sm text-muted-foreground mt-1">Fuel logs above target or requiring review</p>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icons.Receipt className="h-4 w-4 text-rose-400" />
            <h3 className="font-display font-semibold">Open Tickets</h3>
          </div>
          <p className="font-mono text-3xl font-bold text-rose-400">{data.open_tickets}</p>
          <p className="text-sm text-muted-foreground mt-1">Unresolved fines & penalties</p>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Icons.AlertTriangle className="h-4 w-4 text-amber-400" />
            <h3 className="font-display font-semibold">Vehicle Alerts</h3>
          </div>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {data.alert_list?.length ? (
              data.alert_list.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="font-mono">{a.van_reg}</span>
                  <span className="text-muted-foreground">{a.type} · {ukDate(a.date)}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">All vehicles compliant</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
